import BaseState from "../../interfaces/BaseState";

export type FetchUserContractsRequestPayload = {
    userId: number;
    /** If true, do not show loading spinner. */
    background?: boolean;
};

export type FetchUserContractsSuccessPayload = {
    userId: number;
    contracts: UserContracts;
    /** If true, do not decrement loading spinner counter. Value should be copied from {@link FetchUserContractsRequestPayload.background} */
    background: boolean;
};

export type FetchUserContractsFailurePayload = {
    /** If true, do not decrement loading spinner counter. Value should be copied from {@link FetchUserContractsRequestPayload.background} */
    background: boolean;
};

export type FetchCompanyContractsRequestPayload = {
    companyId: number;
    background?: boolean;
};

export type FetchCompanyContractsSuccessPayload = {
    companyId: number;
    companyContracts: CompanyContracts;
    background: boolean;
};

export type UpdateCompanyFrameworkContractPayload = {
    companyId: number;
    coversSubscriptionContract: boolean;
    coversHousingCompanyDpa: boolean;
    coversManagementCompanyDpa: boolean;
};

export type UpdateCompanyFrameworkContractSuccessPayload = {
    companyId: number;
    frameworkContract: FrameworkContract;
};

export type Contract = {
    id: number;
    createdAt: string;
    updatedAt: string;
    providerId: string;
    providerName: string;
    providerContractId: string;
    state: ContractState;
};

/**
 * Represent the status of contract coverage for a specific contract requirement.
 *
 * @param contract The applicable contract to cover this requirement, if any.
 * @param coveredByFramework The applicable framework contract that covers this requirement, if any.
 * @param coveredByUserContract Whether the contract is covered by a user-specific contract elsewhere.
 * @param isCovered Whether the requirement is covered by any contract.
 *
 */
export type ContractCoverage = {
    contract: Contract | null;
    coveredByFramework: FrameworkContract | null;
    coveredByUserContract: boolean;
    isCovered: boolean;
};

export type FrameworkContract = {
    companyId: number;
    updatedAt: string | null;
    coversSubscriptionContract: boolean;
    coversHousingCompanyDpa: boolean;
    coversManagementCompanyDpa: boolean;
};

export enum ContractState {
    UNKNOWN,
    PENDING,
    CANCELLED,
    ACTIVE,
}

export type UserContracts = {
    principalManagerDpaContracts: Contract[];
    companySubscriptionContractsByCompanyId: Record<number, Contract[]>;
    companyDpaContractsByCompanyId: Record<number, Contract[]>;
    frameworkContractsByCompanyId: Record<number, FrameworkContract | null>;
};

export type CompanyContracts = {
    userDpaContracts: Contract[];
    companySubscriptionContracts: Contract[];
    companyDpaContracts: Contract[];
    frameworkContract: FrameworkContract | null;
};

export interface ContractsState extends BaseState {
    userContractsByUserId: Record<number, UserContracts>;
    companyContractsByCompanyId: Record<number, CompanyContracts>;
}

export interface CreatePrincipalManagerDpaRequestPayload {
    userId: number;
    managementCompanyName: string;
    managementCompanyBusinessId: string;
    managementCompanyStreetAddress: string;
    managementCompanyZipCode: string;
    managementCompanyTown: string;
}

export interface CreatePrincipalManagerDpaRequestSuccessPayload {
    userId: number;
    created: Contract;
}

export interface CreateCompanySubscriptionRequestPayload {
    userId: number; // used to refresh the user contracts state after creation
    companyId: number;
    housingCompanyBusinessId: string;
    housingCompanyStreetAddress: string;
    housingCompanyZipCode: string;
    housingCompanyTown: string;
    managementCompanyName: string;
    managementCompanyBusinessId: string;
    managementCompanyStreetAddress: string;
    managementCompanyZipCode: string;
    managementCompanyTown: string;
    managerPhone: string;
}

export interface CreateCompanySubscriptionRequestSuccessPayload {
    companyId: number;
    created: Contract;
}

export interface CreateCompanyDpaRequestPayload {
    userId: number; // used to refresh the user contracts state after creation
    companyId: number;
    businessId: string;
    streetAddress: string;
    zipCode: string;
    town: string;
}

export interface CreateCompanyDpaRequestSuccessPayload {
    companyId: number;
    created: Contract;
}

export interface SendInviteeSignRequestPayload {
    contractId: number;
}

export interface SendInviteeSignRequestSuccessPayload {
    contractId: number;
}

export type CompanyContractsViewData = {
    /**
     * The company's subscription contract coverage.
     */
    subscriptionCoverage: ContractCoverage;

    /**
     * The company's DPA contract coverage.
     */
    dpaCoverage: ContractCoverage;

    /**
     *  The company's management DPA contract coverage.
     */
    managementCompanyDpaCoverage: ContractCoverage;
};
